// src/services/systemConfigService.ts
import { ref, reactive } from "vue";
import { i18n, type AvailableLanguages } from "@/i18n";
import type {
  SystemsConfigFile,
  RuntimeSystemConfig,
  ServiceConfig,
  SystemConfigChangeEvent,
} from "@/types/system";
import { reportError, reportMessage } from "@/utils/sentry";
import { normalizePath, pathStartsWith } from "@/utils/path";

class SystemConfigService {
  private configFile = ref<SystemsConfigFile | null>(null);
  private lastFetchTime = ref<Date | null>(null);
  private isLoading = ref(false);
  private fetchError = ref<string | null>(null);

  // 配置变更事件监听器
  private changeListeners: ((event: SystemConfigChangeEvent) => void)[] = [];

  // 缓存的运行时配置
  private _runtimeSystems = reactive<Record<string, RuntimeSystemConfig>>({});
  private _serviceConfigs = reactive<Record<string, ServiceConfig>>({});

  constructor() {
    this.init();
  }

  private async init() {
    try {
      await this.loadConfig();
      this.startPeriodicCheck();
    } catch (error) {
      reportError(error instanceof Error ? error : new Error(String(error)), {
        component: "SystemConfigService",
        action: "init",
      });
    }
  }

  public async loadConfig(): Promise<void> {
    if (this.isLoading.value) return;

    this.isLoading.value = true;
    this.fetchError.value = null;

    try {
      const configUrl = this.getConfigUrl();
      const response = await fetch(configUrl, {
        cache: "no-cache",
        headers: {
          "Cache-Control": "no-cache",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch config: ${response.status} ${response.statusText}`,
        );
      }

      const configData: SystemsConfigFile = await response.json();

      // 验证配置文件格式
      this.validateConfig(configData);

      // 应用环境覆盖
      const processedConfig = this.applyEnvironmentOverrides(configData);

      // 更新配置
      const oldConfig = this.configFile.value;
      this.configFile.value = processedConfig;
      this.lastFetchTime.value = new Date();

      // 重新构建运行时配置
      this.buildRuntimeConfigs();

      // 注册 i18n 翻译
      this.registerI18nTranslations();

      // 触发变更事件
      if (oldConfig) {
        this.emitConfigChangeEvent(oldConfig, processedConfig);
      }

      reportMessage("系统配置加载成功", "info", {
        component: "SystemConfigService",
        action: "loadConfig",
        configFile: this.getConfigFileName(),
        systemCount: Object.keys(processedConfig.systems).length,
        serviceCount: Object.keys(processedConfig.services).length,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      this.fetchError.value = errorMessage;

      reportError(error instanceof Error ? error : new Error(errorMessage), {
        component: "SystemConfigService",
        action: "loadConfig",
        configFile: this.getConfigFileName(),
      });

      // 如果指定的配置文件加载失败，尝试加载默认配置
      const currentConfigFile = this.getConfigFileName();
      if (currentConfigFile !== "systems.json" && !this.configFile.value) {
        try {
          await this.loadFallbackConfig();
        } catch {
          // 如果默认配置也加载失败，使用硬编码的回退配置
          this.useFallbackConfig();
        }
      } else if (!this.configFile.value) {
        // 如果没有缓存配置，使用默认配置
        this.useFallbackConfig();
      }
    } finally {
      this.isLoading.value = false;
    }
  }

  private async loadFallbackConfig(): Promise<void> {
    const baseUrl = import.meta.env.VITE_BASE_URL || "/";
    const fallbackUrl = baseUrl.endsWith("/")
      ? `${baseUrl}config/systems.json?t=${Date.now()}`
      : `${baseUrl}/config/systems.json?t=${Date.now()}`;

    const response = await fetch(fallbackUrl, {
      cache: "no-cache",
      headers: {
        "Cache-Control": "no-cache",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch fallback config: ${response.status} ${response.statusText}`,
      );
    }

    const configData: SystemsConfigFile = await response.json();
    this.validateConfig(configData);

    const processedConfig = this.applyEnvironmentOverrides(configData);
    this.configFile.value = processedConfig;
    this.lastFetchTime.value = new Date();

    this.buildRuntimeConfigs();
    this.registerI18nTranslations();

    reportMessage("使用默认配置文件作为回退", "warning", {
      component: "SystemConfigService",
      action: "loadFallbackConfig",
      originalConfigFile: this.getConfigFileName(),
      fallbackConfigFile: "systems.json",
    });
  }

  private getConfigFileName(): string {
    // 1. 优先使用 VITE_SYSTEM_CONFIG 环境变量指定的配置
    const explicitConfig = import.meta.env.VITE_SYSTEM_CONFIG;
    if (explicitConfig) {
      return `systems-${explicitConfig}.json`;
    }

    // 2. 根据 MODE 自动选择配置文件
    const mode = import.meta.env.MODE;
    const modeConfigMap: Record<string, string> = {
      development: "systems-abd-preview.json",
      preview: "systems-abd-preview.json",
      production: "systems-abd-prod.json",
    };

    if (modeConfigMap[mode]) {
      return modeConfigMap[mode];
    }

    // 3. 默认回退到 systems.json
    return "systems.json";
  }

  private getConfigUrl(): string {
    const baseUrl = import.meta.env.VITE_BASE_URL || "/";
    const configFileName = this.getConfigFileName();
    const configPath = `config/${configFileName}`;

    // 确保路径正确拼接
    const url = baseUrl.endsWith("/")
      ? `${baseUrl}${configPath}`
      : `${baseUrl}/${configPath}`;

    // 添加时间戳防止缓存
    return `${url}?t=${Date.now()}`;
  }

  private validateConfig(config: SystemsConfigFile): void {
    if (!config.version || !config.systems || !config.services) {
      throw new Error("Invalid config file format: missing required fields");
    }

    // 验证系统配置
    for (const [systemName, systemConfig] of Object.entries(config.systems)) {
      if (!systemConfig.name || !systemConfig.path || !systemConfig.i18n) {
        throw new Error(
          `Invalid system config for ${systemName}: missing required fields`,
        );
      }

      // 验证 i18n 翻译
      if (!systemConfig.i18n["zh-CN"] || !systemConfig.i18n["en-GB"]) {
        throw new Error(
          `Invalid i18n config for system ${systemName}: missing translations`,
        );
      }
    }

    // 验证服务配置
    for (const [serviceName, serviceConfig] of Object.entries(
      config.services,
    )) {
      if (!serviceConfig.id || !serviceConfig.i18n) {
        throw new Error(
          `Invalid service config for ${serviceName}: missing required fields`,
        );
      }
    }
  }

  private applyEnvironmentOverrides(
    config: SystemsConfigFile,
  ): SystemsConfigFile {
    const env = import.meta.env.MODE;
    const overrides = config.environmentOverrides?.[env];

    if (!overrides?.systems) {
      return config;
    }

    const newConfig = { ...config };
    newConfig.systems = { ...config.systems };

    for (const [systemName, override] of Object.entries(overrides.systems)) {
      if (newConfig.systems[systemName]) {
        newConfig.systems[systemName] = {
          ...newConfig.systems[systemName],
          ...override,
        };
      }
    }

    return newConfig;
  }

  private buildRuntimeConfigs(): void {
    if (!this.configFile.value) return;

    // 清空现有配置
    Object.keys(this._runtimeSystems).forEach((key) => {
      delete this._runtimeSystems[key];
    });
    Object.keys(this._serviceConfigs).forEach((key) => {
      delete this._serviceConfigs[key];
    });

    // 构建运行时系统配置
    for (const [systemName, systemConfig] of Object.entries(
      this.configFile.value.systems,
    )) {
      const currentLocale = i18n.global.locale.value as AvailableLanguages;
      const i18nData =
        systemConfig.i18n[currentLocale] || systemConfig.i18n["en-GB"];

      // 处理 heroicon
      const heroicon = i18nData.heroicon || systemConfig.heroicon;

      this._runtimeSystems[systemName] = {
        name: systemConfig.name,
        path: systemConfig.path,
        enabled: systemConfig.enabled,
        callback: systemConfig.callback,
        services: systemConfig.services,
        title: i18nData.title,
        description: i18nData.description,
        comingSoon: systemConfig.comingSoon,
        heroicon,
      };
    }

    // 复制服务配置
    Object.assign(this._serviceConfigs, this.configFile.value.services);
  }

  private getSystemLogo(): string {
    if (this.configFile.value?.logo) {
      const logoPath = this.configFile.value.logo;

      // 如果是完整的HTTP/HTTPS URL，直接使用
      if (logoPath.startsWith("http://") || logoPath.startsWith("https://")) {
        return logoPath;
      }

      // 获取应用基础路径
      const baseUrl = import.meta.env.VITE_BASE_URL || "/";
      const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;

      // 如果是绝对路径（以/开头）且已经包含baseUrl，直接使用
      if (
        logoPath.startsWith("/") &&
        logoPath.startsWith(normalizedBaseUrl.slice(0, -1))
      ) {
        return logoPath;
      }

      // 如果是绝对路径（以/开头）但不包含baseUrl，拼接baseUrl
      if (logoPath.startsWith("/")) {
        const cleanPath = logoPath.substring(1);
        return `${normalizedBaseUrl}${cleanPath}`;
      }

      // 如果是相对路径，拼接baseUrl
      return `${normalizedBaseUrl}${logoPath}`;
    }

    // 当没有配置时，默认使用scnu-logo.svg
    const baseUrl = import.meta.env.VITE_BASE_URL || "/";
    const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
    return `${normalizedBaseUrl}assets/scnu-logo.svg`;
  }

  private registerI18nTranslations(): void {
    if (!this.configFile.value) return;

    try {
      const translations: Record<AvailableLanguages, any> = {
        "zh-CN": {},
        "en-GB": {},
      };

      // 添加系统翻译
      translations["zh-CN"].systems = {};
      translations["en-GB"].systems = {};

      for (const [systemName, systemConfig] of Object.entries(
        this.configFile.value.systems,
      )) {
        translations["zh-CN"].systems[systemName] = systemConfig.i18n["zh-CN"];
        translations["en-GB"].systems[systemName] = systemConfig.i18n["en-GB"];
      }

      // 添加服务翻译
      translations["zh-CN"].services = {};
      translations["en-GB"].services = {};

      for (const [serviceName, serviceConfig] of Object.entries(
        this.configFile.value.services,
      )) {
        translations["zh-CN"].services[serviceName] =
          serviceConfig.i18n["zh-CN"];
        translations["en-GB"].services[serviceName] =
          serviceConfig.i18n["en-GB"];
      }

      // 合并到现有的 i18n 实例
      for (const [locale, trans] of Object.entries(translations)) {
        i18n.global.mergeLocaleMessage(locale as AvailableLanguages, trans);
      }
    } catch (error) {
      reportError(error instanceof Error ? error : new Error(String(error)), {
        component: "SystemConfigService",
        action: "registerI18nTranslations",
      });
    }
  }

  private useFallbackConfig(): void {
    // 当完全没有配置时，只提供基本配置和logo，不提供任何系统
    const fallbackConfig: SystemsConfigFile = {
      version: "fallback",
      lastUpdated: new Date().toISOString(),
      logo: "assets/scnu-logo.svg", // 使用默认logo
      systems: {}, // 没有配置时不提供任何系统
      services: {},
    };

    this.configFile.value = fallbackConfig;
    this.buildRuntimeConfigs();
    this.registerI18nTranslations();

    reportMessage("使用回退配置：未找到系统配置，显示空状态", "warning", {
      component: "SystemConfigService",
      action: "useFallbackConfig",
    });
  }

  private emitConfigChangeEvent(
    oldConfig: SystemsConfigFile,
    newConfig: SystemsConfigFile,
  ): void {
    const changes: string[] = [];

    // 检查系统变更
    const oldSystems = Object.keys(oldConfig.systems);
    const newSystems = Object.keys(newConfig.systems);

    if (
      oldSystems.length !== newSystems.length ||
      !oldSystems.every((sys) => newSystems.includes(sys))
    ) {
      changes.push("系统列表已更新");
    }

    // 检查服务变更
    const oldServices = Object.keys(oldConfig.services);
    const newServices = Object.keys(newConfig.services);

    if (
      oldServices.length !== newServices.length ||
      !oldServices.every((srv) => newServices.includes(srv))
    ) {
      changes.push("服务列表已更新");
    }

    if (changes.length > 0) {
      const event: SystemConfigChangeEvent = {
        type: "systems",
        changes,
        timestamp: new Date(),
      };

      this.changeListeners.forEach((listener) => {
        try {
          listener(event);
        } catch (error) {
          reportError(
            error instanceof Error ? error : new Error(String(error)),
            {
              component: "SystemConfigService",
              action: "emitConfigChangeEvent",
            },
          );
        }
      });
    }
  }

  private startPeriodicCheck(): void {
    // 每30秒检查一次配置更新
    setInterval(() => {
      this.checkForUpdates();
    }, 30000);
  }

  private async checkForUpdates(): Promise<void> {
    if (this.isLoading.value) return;

    try {
      const configUrl = this.getConfigUrl();
      const response = await fetch(configUrl, {
        method: "HEAD",
        cache: "no-cache",
      });

      if (response.ok) {
        const lastModified = response.headers.get("Last-Modified");
        if (lastModified && this.lastFetchTime.value) {
          const serverTime = new Date(lastModified);
          if (serverTime > this.lastFetchTime.value) {
            await this.loadConfig();
          }
        }
      }
    } catch {
      // 静默处理检查错误，避免过多日志
    }
  }

  public get systems(): Readonly<Record<string, RuntimeSystemConfig>> {
    return this._runtimeSystems;
  }

  public get services(): Readonly<Record<string, ServiceConfig>> {
    return this._serviceConfigs;
  }

  public getSystemConfig(systemName: string): RuntimeSystemConfig | undefined {
    return this._runtimeSystems[systemName];
  }

  public get enabledSystems(): RuntimeSystemConfig[] {
    return Object.values(this._runtimeSystems).filter(
      (system) => system.enabled,
    );
  }

  public getSystemServices(systemName: string): ServiceConfig[] {
    const system = this._runtimeSystems[systemName];
    if (!system) return [];

    return system.services
      .map((serviceInfo) => this._serviceConfigs[serviceInfo.name])
      .filter(Boolean);
  }

  public getSystemByPath(path: string): RuntimeSystemConfig | undefined {
    // 标准化输入路径
    const normalizedPath = normalizePath(path);

    return Object.values(this._runtimeSystems).find((system) => {
      const normalizedSystemPath = normalizePath(system.path);
      return pathStartsWith(normalizedPath, normalizedSystemPath);
    });
  }

  public async reloadConfig(): Promise<void> {
    await this.loadConfig();
  }

  public onConfigChange(
    listener: (event: SystemConfigChangeEvent) => void,
  ): void {
    this.changeListeners.push(listener);
  }

  public offConfigChange(
    listener: (event: SystemConfigChangeEvent) => void,
  ): void {
    const index = this.changeListeners.indexOf(listener);
    if (index > -1) {
      this.changeListeners.splice(index, 1);
    }
  }

  public get isConfigLoading(): boolean {
    return this.isLoading.value;
  }

  public get configError(): string | null {
    return this.fetchError.value;
  }

  public get currentConfigFile(): string {
    return this.getConfigFileName();
  }

  public get lastConfigUpdate(): Date | null {
    return this.lastFetchTime.value;
  }

  public get configEnvironment(): string | undefined {
    return this.configFile.value?.environment;
  }

  public get systemLogo(): string {
    return this.getSystemLogo();
  }
}

// 全局配置服务实例
export const systemConfigService = new SystemConfigService();
